import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';

const emptyBundleItem = { product: '', quantity: '1' };
const emptyBundlePriceHistoryRow = { effectiveFrom: '', price: '' };
const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';

const getProductLabel = (product) => `${product.category} - ${product.productName}`;
const normalizeSearchText = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const getLatestProductUnitPrice = (product) => {
    if (!product) return 0;
    const h = product.unitPriceHistory;
    if (Array.isArray(h) && h.length > 0) {
        const latest = [...h]
            .filter((row) => row?.effectiveFrom && row?.unitPrice != null)
            .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))[0];
        if (latest != null) return Number(latest.unitPrice);
    }
    return Number(product.unitPrice || 0);
};

const formatLatestProductUnitPrice = (product) => {
    if (!product) return 'N/A';
    const up = getLatestProductUnitPrice(product);
    return Number.isFinite(up) ? `$${Number(up).toFixed(2)}` : 'N/A';
};

const SimonBundleModal = ({ mode = 'create', bundle = null, products = [], onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        bundleName: '',
        bundlePriceHistory: [{ ...emptyBundlePriceHistoryRow }],
        products: [{ ...emptyBundleItem }],
        isActive: true
    });
    const [productSearchQueries, setProductSearchQueries] = useState(['']);
    const [activeSearchIndex, setActiveSearchIndex] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (mode === 'edit' && bundle) {
            const normalizedProducts = bundle.products?.length
                ? bundle.products.map((item) => ({
                    product: item.product?._id || item.product || '',
                    quantity: item.quantity != null ? String(item.quantity) : '1'
                }))
                : [{ ...emptyBundleItem }];

            setFormData({
                bundleName: bundle.bundleName || '',
                bundlePriceHistory: bundle.bundlePriceHistory?.length
                    ? [...bundle.bundlePriceHistory]
                        .sort((a, b) => new Date(a.effectiveFrom) - new Date(b.effectiveFrom))
                        .map((row) => ({
                            effectiveFrom: row?.effectiveFrom ? new Date(row.effectiveFrom).toISOString().split('T')[0] : '',
                            price: row?.price != null ? String(row.price) : ''
                        }))
                    : [{ ...emptyBundlePriceHistoryRow }],
                products: normalizedProducts,
                isActive: bundle.isActive !== undefined ? Boolean(bundle.isActive) : true
            });

            setProductSearchQueries(
                normalizedProducts.map((item) => {
                    const matchedProduct = products.find((product) => product._id === item.product);
                    return matchedProduct ? getProductLabel(matchedProduct) : '';
                })
            );
        }
    }, [mode, bundle, products]);

    const updateBundleItem = (index, field, value) => {
        const next = [...formData.products];
        next[index] = { ...(next[index] || emptyBundleItem), [field]: value };
        setFormData((prev) => ({ ...prev, products: next }));
    };

    const addBundleItem = () => {
        setFormData((prev) => ({ ...prev, products: [...prev.products, { ...emptyBundleItem }] }));
        setProductSearchQueries((prev) => [...prev, '']);
    };

    const removeBundleItem = (index) => {
        const next = formData.products.filter((_, itemIndex) => itemIndex !== index);
        const nextQueries = productSearchQueries.filter((_, itemIndex) => itemIndex !== index);
        setFormData((prev) => ({ ...prev, products: next.length ? next : [{ ...emptyBundleItem }] }));
        setProductSearchQueries(nextQueries.length ? nextQueries : ['']);
    };

    const updateProductSearchQuery = (index, value) => {
        const next = [...productSearchQueries];
        next[index] = value;
        setProductSearchQueries(next);
    };

    const updateBundlePriceHistoryRow = (index, field, value) => {
        const next = [...formData.bundlePriceHistory];
        next[index] = { ...(next[index] || emptyBundlePriceHistoryRow), [field]: value };
        setFormData((prev) => ({ ...prev, bundlePriceHistory: next }));
    };

    const addBundlePriceHistoryRow = () => {
        setFormData((prev) => ({
            ...prev,
            bundlePriceHistory: [...prev.bundlePriceHistory, { ...emptyBundlePriceHistoryRow }]
        }));
    };

    const removeBundlePriceHistoryRow = (index) => {
        const next = formData.bundlePriceHistory.filter((_, rowIndex) => rowIndex !== index);
        setFormData((prev) => ({
            ...prev,
            bundlePriceHistory: next.length ? next : [{ ...emptyBundlePriceHistoryRow }]
        }));
    };

    const selectProductFromSearch = (index, productId) => {
        const selectedProduct = products.find((product) => product._id === productId);
        updateBundleItem(index, 'product', productId);
        updateProductSearchQuery(index, selectedProduct ? getProductLabel(selectedProduct) : '');
        setActiveSearchIndex(-1);
    };

    const getSelectedProduct = (productId) => products.find((product) => product._id === productId);

    const getFilteredProducts = (query) => {
        const trimmed = String(query || '').trim().toLowerCase();
        if (trimmed.length <= 3) return [];
        const normalizedQuery = normalizeSearchText(trimmed);
        return products.filter((product) => {
            const label = getProductLabel(product).toLowerCase();
            const normalizedLabel = normalizeSearchText(label);
            const productName = String(product?.productName || '').toLowerCase();
            const category = String(product?.category || '').toLowerCase();

            return (
                label.includes(trimmed) ||
                productName.includes(trimmed) ||
                category.includes(trimmed) ||
                normalizedLabel.includes(normalizedQuery)
            );
        });
    };

    const bundlePriceValue = (() => {
        const validRows = formData.bundlePriceHistory
            .map((row) => ({
                effectiveFrom: row.effectiveFrom ? new Date(row.effectiveFrom) : null,
                price: row.price !== '' ? Number.parseFloat(row.price) : NaN
            }))
            .filter((row) => row.effectiveFrom && !Number.isNaN(row.price))
            .sort((a, b) => a.effectiveFrom - b.effectiveFrom);
        if (!validRows.length) return 0;
        return validRows[validRows.length - 1].price;
    })();
    const productTotalPrice = formData.products.reduce((sum, item) => {
        const selectedProduct = getSelectedProduct(item.product);
        const unitPrice = getLatestProductUnitPrice(selectedProduct);
        const quantity = Number.parseInt(item.quantity || '0', 10);
        return sum + (unitPrice * (Number.isNaN(quantity) ? 0 : quantity));
    }, 0);
    const totalSaved = productTotalPrice - (Number.isNaN(bundlePriceValue) ? 0 : bundlePriceValue);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                bundleName: formData.bundleName.trim(),
                bundlePriceHistory: formData.bundlePriceHistory
                    .map((row) => ({
                        effectiveFrom: row.effectiveFrom || undefined,
                        price: row.price !== '' ? parseFloat(row.price) : undefined
                    }))
                    .filter((row) => row.effectiveFrom && typeof row.price === 'number' && !Number.isNaN(row.price)),
                isActive: formData.isActive,
                products: formData.products
                    .map((item) => ({
                        product: item.product,
                        quantity: parseInt(item.quantity || '0', 10)
                    }))
                    .filter((item) => item.product && item.quantity > 0)
            };

            const token = localStorage.getItem('token');
            const request = mode === 'edit'
                ? axios.put(`${process.env.REACT_APP_API_URL}/api/simonbundles/${bundle._id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                })
                : axios.post(`${process.env.REACT_APP_API_URL}/api/simonbundles`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                });

            const response = await request;
            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to ${mode} Simon bundle: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8 min-w-0 overflow-x-hidden">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-6">
                        <h2 className="text-xl font-bold">{mode === 'edit' ? 'Edit Simon Bundle' : 'Create Simon Bundle'}</h2>
                        <label className="inline-flex items-center gap-2">
                            <input
                                type="checkbox"
                                checked={formData.isActive}
                                onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
                            />
                            <span className="text-sm font-medium text-gray-700">Active</span>
                        </label>
                    </div>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.bundleName}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, bundleName: e.target.value }))}
                                    className={inputClassName}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Bundle Price History</label>
                                <div className="space-y-2">
                                    {formData.bundlePriceHistory.map((row, rowIndex) => (
                                        <div key={`bundle-price-row-${rowIndex}`} className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_auto] gap-2">
                                            <input
                                                type="date"
                                                value={row.effectiveFrom}
                                                onChange={(e) => updateBundlePriceHistoryRow(rowIndex, 'effectiveFrom', e.target.value)}
                                                className={inputClassName}
                                            />
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={row.price}
                                                onChange={(e) => updateBundlePriceHistoryRow(rowIndex, 'price', e.target.value)}
                                                className={inputClassName}
                                                placeholder="Price"
                                            />
                                            <div className="flex items-center gap-2">
                                                {formData.bundlePriceHistory.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => removeBundlePriceHistoryRow(rowIndex)}
                                                        className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={addBundlePriceHistoryRow}
                                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                    >
                                        + Add Price Date Row
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Bundle Products</label>
                            <div className="space-y-3">
                                {formData.products.map((item, index) => (
                                    <div key={`bundle-item-${index}`} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="block text-sm font-medium text-gray-700">
                                                Bundle Product {index + 1}
                                            </label>
                                            {formData.products.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeBundleItem(index)}
                                                    className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>

                                        <div className="relative mb-3">
                                            <input
                                                type="text"
                                                value={productSearchQueries[index] || ''}
                                                onChange={(e) => updateProductSearchQuery(index, e.target.value)}
                                                onFocus={() => setActiveSearchIndex(index)}
                                                placeholder="Type product (min 4 chars)"
                                                className={inputClassName}
                                            />
                                            {activeSearchIndex === index && String(productSearchQueries[index] || '').trim().length > 3 && (
                                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow max-h-56 overflow-y-auto">
                                                    {getFilteredProducts(productSearchQueries[index]).length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">No products found</div>
                                                    ) : (
                                                        getFilteredProducts(productSearchQueries[index]).map((product) => (
                                                            <button
                                                                key={product._id}
                                                                type="button"
                                                                onClick={() => selectProductFromSearch(index, product._id)}
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-700 font-bold"
                                                            >
                                                                {getProductLabel(product)}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white">
                                                <span className="text-sm text-gray-700 mr-2">Unit Price:</span>
                                                <span className="text-blue-700 font-bold">
                                                    {formatLatestProductUnitPrice(getSelectedProduct(item.product))}
                                                </span>
                                            </div>
                                            <div className="flex items-center px-3 py-2 border border-gray-300 rounded-md bg-white">
                                                <span className="text-sm text-gray-700 mr-2">Quantity:</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={(e) => updateBundleItem(index, 'quantity', e.target.value)}
                                                    className="w-full text-blue-700 font-bold focus:outline-none bg-transparent"
                                                    placeholder="Quantity"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={addBundleItem}
                                className="mt-3 px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                + Add Product
                            </button>
                        </div>

                        <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-4">
                            <div className="text-sm text-gray-700">
                                <div className="mb-1">
                                    <span className="font-semibold">Bundle Price: </span>
                                    <span className="text-blue-700 font-bold">${(Number.isNaN(bundlePriceValue) ? 0 : bundlePriceValue).toFixed(2)}</span>
                                </div>
                                <div className="mb-1">
                                    <span className="font-semibold">Product Total Price: </span>
                                    <span className="text-blue-700 font-bold">${productTotalPrice.toFixed(2)}</span>
                                </div>
                                <div>
                                    <span className="font-semibold">Total Saved exclud. Del Fees: </span>
                                    <span className="text-blue-700 font-bold">${totalSaved.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            disabled={loading}
                        >
                            {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SimonBundleModal;
